import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Cpu, Database, Phone, Zap, HelpCircle } from 'lucide-react';
import { billingAPI } from '../../lib/api';
import { useBillingCurrency } from '../../hooks/useBillingCurrency';

const CATEGORY_META = {
  ai: { label: 'AI Intelligence', icon: Cpu, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
  data: { label: 'Data & Enrichment', icon: Database, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  voice: { label: 'Voice Calls', icon: Phone, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
  automation: { label: 'Automation', icon: Zap, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  other: { label: 'Other', icon: HelpCircle, color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20' },
};

export function ActionPricingTab() {
  const billing = useBillingCurrency();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getActionPricing();
        setPricing(res.data?.pricing || []);
      } catch {
        setPricing([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
      </div>
    );
  }

  const grouped = {};
  for (const item of pricing) {
    const cat = item.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  return (
    <div className="space-y-6 p-1" data-testid="action-pricing-tab">
      <div>
        <h3 className="text-lg font-semibold">Per-Action Credit Pricing</h3>
        <p className="text-sm text-muted-foreground mt-1">Cost breakdown for every AI and data action in your CRM</p>
      </div>

      {Object.entries(grouped).map(([cat, items]) => {
        const meta = CATEGORY_META[cat] || CATEGORY_META.other;
        const Icon = meta.icon;
        return (
          <Card key={cat} className={`border ${meta.bg}`} data-testid={`pricing-category-${cat}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon className={`h-5 w-5 ${meta.color}`} />
                {meta.label}
              </CardTitle>
              <CardDescription>{items.length} action{items.length !== 1 ? 's' : ''}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Action</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground hidden sm:table-cell">Description</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">{billing.currency === 'INR' ? 'Cost (₹)' : 'Cost (USD)'}</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.action} className="border-b border-border/20 hover:bg-muted/30 transition-colors" data-testid={`pricing-row-${item.action}`}>
                        <td className="py-2.5 px-3 font-medium">{item.label}</td>
                        <td className="py-2.5 px-3 text-muted-foreground hidden sm:table-cell">{item.description}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {item.cost_usd === 0 ? (
                            <Badge variant="outline" className="text-green-400 border-green-500/30">Free</Badge>
                          ) : (
                            <span>{billing.format(item.cost_usd, { maxFractionDigits: 4 })}</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {item.cost_credits === 0 ? '—' : item.cost_credits}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <p className="text-xs text-muted-foreground text-center">
        1 Credit = {billing.format(0.01, { maxFractionDigits: 2 })}{billing.currency === 'INR' ? ' INR' : ' USD'}. Prices include 30% platform margin on data provider costs.
      </p>
    </div>
  );
}
